import {assertDataPresent} from '@github-ui/assert-data-present'
import {useAnalytics, useClickAnalytics} from '@github-ui/use-analytics'
import {ActionList, Link} from '@primer/react'
import {lazy, Suspense, useRef, useState} from 'react'

import {useMergeMethodContext} from '../../../contexts/MergeMethodContext'
import {mergeButtonText} from '../../../helpers/merge-button-text'
import {
  MergeMethod,
  MergeAction,
  type ViewerMergeActions,
  type ViewerMergeMethods,
  type PullRequestMergeConditionResult,
} from '../../../types'
import {ConfirmMerge} from '../../ConfirmMerge'
import {ButtonWithDropdown} from '../common/ButtonWithDropdown'
import {MergeDropdownOption} from './MergeDropdownOption'
import type {PullRequestMergeRequirementsPayload} from '../../../page-data/payloads/merge-box'
import {Status} from '../../../helpers/mergeability-status'
import {MergeSectionActions} from './MergeSectionActions'
import {PreviewFeedbackMenu} from './PreviewFeedbackMenu'
import type {MergeBoxRollupStatus} from '../../../helpers/merge-box-status-calculator/types'
import {
  buildPullRequestUserActionContext,
  type PullRequestUserActionStack,
} from '@github-ui/pull-requests/analytics/pull-request-user-action'

const CommandLineInstructionsDialog = lazy(() => import('./command-line-instructions/CommandLineInstructionsDialog'))

/**
 * Return a list of merge methods to show in the "select merge method" dropdown
 */
export function allowableMergeMethods({mergeMethods}: {mergeMethods: ViewerMergeMethods}): MergeMethod[] {
  return mergeMethods.reduce<MergeMethod[]>((allowedMergeMethods, method) => {
    if (method?.allowableStatus === 'ALLOWED' || method?.allowableStatus === 'ALLOWED_WITH_BYPASS') {
      allowedMergeMethods.push(method.name)
    }

    return allowedMergeMethods
  }, [])
}

export type MergeButtonFocusProps = {
  shouldFocusPrimaryMergeButton: boolean
  setShouldFocusPrimaryMergeButton: (shouldFocus: boolean) => void
}

export type DirectMergeActionsSectionProps = {
  baseRefName: string
  canUserPushToBase: boolean
  defaultCommitAuthorEmail: PullRequestMergeRequirementsPayload['defaultCommitAuthorEmail']
  commitMessageBody: PullRequestMergeRequirementsPayload['commitMessageBody']
  commitMessageHeadline: PullRequestMergeRequirementsPayload['commitMessageHeadline']
  conflictsCondition: {result: PullRequestMergeConditionResult} | undefined
  handleConfirmingMergeInfo: (isConfirming: boolean) => void
  headRepository: {ownerLogin: string; name: string} | null
  helpUrl: string
  isAdminBypassToggleChecked: boolean
  isAdminBypassToggleVisible: boolean
  isAutoMergeAllowed?: boolean
  isConfirmingMergeInfo: boolean
  isCrossRepo: boolean
  isDraft: boolean
  isStacked?: boolean
  mergeCurrentlyBlockedForStacks?: boolean
  stackedEntriesToMergeCount?: number
  stackMetadata?: PullRequestUserActionStack
  stackPositionInStack?: number
  stackSize?: number
  mergeRequirementsState: PullRequestMergeRequirementsPayload['state']
  mergeable: boolean
  numberOfCommits: number
  mergeBoxRollupStatus: MergeBoxRollupStatus
  status: Status
  viewerMergeActions: ViewerMergeActions
  possibleCommitAuthorEmails: PullRequestMergeRequirementsPayload['possibleCommitAuthorEmails']
} & MergeButtonFocusProps

/**
 * Renders the merge button and provides the ability to select from the available merge methods
 */
export function DirectMergeActionsSection({
  baseRefName,
  canUserPushToBase,
  defaultCommitAuthorEmail,
  commitMessageBody,
  commitMessageHeadline,
  conflictsCondition,
  handleConfirmingMergeInfo,
  headRepository,
  helpUrl,
  isAdminBypassToggleChecked,
  isAdminBypassToggleVisible,
  isAutoMergeAllowed = false,
  isConfirmingMergeInfo,
  isCrossRepo,
  isDraft,
  isStacked = false,
  mergeCurrentlyBlockedForStacks = false,
  stackedEntriesToMergeCount = 1,
  stackMetadata,
  stackPositionInStack,
  stackSize,
  mergeRequirementsState,
  mergeable,
  numberOfCommits,
  mergeBoxRollupStatus,
  setShouldFocusPrimaryMergeButton,
  shouldFocusPrimaryMergeButton,
  status,
  viewerMergeActions,
  possibleCommitAuthorEmails,
}: DirectMergeActionsSectionProps) {
  const {mergeMethod, setMergeMethod} = useMergeMethodContext()
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const {sendAnalyticsEvent} = useAnalytics()
  const [isCommandLineInstructionsOpen, setIsCommandLineInstructionsOpen] = useState(false)
  const returnFocusToCommandLineInstructionsButtonRef = useRef<HTMLButtonElement | null>(null)

  void isDraft
  void mergeRequirementsState

  if (!conflictsCondition) return null
  const directMergeAction = viewerMergeActions.find(action => action.name === MergeAction.DIRECT_MERGE)
  assertDataPresent(directMergeAction)

  const allowedMergeMethods = allowableMergeMethods({mergeMethods: directMergeAction.mergeMethods})
  const canCreateMergeCommit = allowedMergeMethods.includes(MergeMethod.MERGE)
  const canSquashMerge = allowedMergeMethods.includes(MergeMethod.SQUASH)
  const canRebaseMerge = allowedMergeMethods.includes(MergeMethod.REBASE)

  // account for a scenario where the button is checked, then a live update causes the PR's state to update to
  // be mergeable and we stop showing the checkbox
  const isBypassRulesActive = isAdminBypassToggleVisible && isAdminBypassToggleChecked

  const inactiveTooltipTextVerb = status === Status.ChecksPending ? 'pending' : 'failing'
  const inactiveTooltipText = mergeCurrentlyBlockedForStacks
    ? 'Merging is blocked due to failing stack merge requirements'
    : `Merging is blocked due to ${inactiveTooltipTextVerb} merge requirements`
  const shouldSendClickAnalyticsEvent = isStacked && stackMetadata && stackPositionInStack && stackSize

  const confirmMerge = () => {
    handleConfirmingMergeInfo(true)

    if (shouldSendClickAnalyticsEvent) {
      sendClickAnalyticsEvent(
        buildPullRequestUserActionContext({
          category: 'merge_box',
          action: 'stack_merge_click',
          data: {
            stack: stackMetadata,
            merge_method: mergeMethod,
            stack_size: stackSize,
            position_in_stack: stackPositionInStack,
            uses_merge_queue: false,
          },
        }),
      )
    }

    const analyticsEventKey = isAutoMergeAllowed
      ? 'direct_merge_section.auto_merge_click'
      : 'direct_merge_section.direct_merge_click'

    const analyticsEventValue = isAutoMergeAllowed
      ? 'MERGEBOX_AUTO_MERGE_SECTION_MERGE_BUTTON'
      : 'MERGEBOX_DIRECT_MERGE_SECTION_MERGE_BUTTON'

    sendAnalyticsEvent(analyticsEventKey, analyticsEventValue)
  }

  const cancelMerge = () => {
    handleConfirmingMergeInfo(false)
    setShouldFocusPrimaryMergeButton(true)

    const analyticsEventKey = isAutoMergeAllowed
      ? 'direct_merge_section.cancel_auto_merge'
      : 'direct_merge_section.cancel_direct_merge'

    const analyticsEventValue = isAutoMergeAllowed
      ? 'MERGEBOX_AUTO_MERGE_CANCEL_CONFIRMATION_BUTTON'
      : 'MERGEBOX_DIRECT_MERGE_CANCEL_CONFIRMATION_BUTTON'

    sendAnalyticsEvent(analyticsEventKey, analyticsEventValue)
  }

  const handleOpenCommandLineInstructions = () => {
    setIsCommandLineInstructionsOpen(true)
    sendAnalyticsEvent(
      'direct_merge_section.view_command_line_instructions',
      'MERGEBOX_DIRECT_MERGE_SECTION_VIEW_COMMAND_LINE_INSTRUCTIONS_BUTTON',
    )
  }

  const updateMergeMethod = (newMergeMethod: MergeMethod, analyticsEvent: string) => {
    setMergeMethod(newMergeMethod)
    sendAnalyticsEvent(analyticsEvent, 'MERGEBOX_DIRECT_MERGE_SECTION_MERGE_METHOD_MENU_ITEM')
  }

  if (isConfirmingMergeInfo) {
    return (
      <ConfirmMerge
        defaultCommitAuthorEmail={defaultCommitAuthorEmail || ''}
        commitMessageBody={commitMessageBody || ''}
        commitMessageHeadline={commitMessageHeadline || ''}
        defaultBranchName={baseRefName}
        handleConfirmingMergeInfo={handleConfirmingMergeInfo}
        isBypassMerge={isAdminBypassToggleChecked}
        isStacked={isStacked}
        stackedEntriesToMergeCount={stackedEntriesToMergeCount}
        selectedMergeMethod={mergeMethod}
        onCancel={cancelMerge}
        isAutoMergeAllowed={isAutoMergeAllowed}
        possibleCommitAuthorEmails={possibleCommitAuthorEmails}
      />
    )
  }

  const isMergingMultiplePullRequests = isStacked && stackedEntriesToMergeCount > 1
  const stackedPRsButtonCount = isMergingMultiplePullRequests ? stackedEntriesToMergeCount : undefined

  let mergeOptionText
  if (isMergingMultiplePullRequests) {
    mergeOptionText = `All commits from these ${stackedEntriesToMergeCount} branches will be added to the base branch via merge commits.`
  } else {
    mergeOptionText = `All commits from this branch will be added to the base branch via a merge commit.`
  }

  let squashOptionText
  if (isMergingMultiplePullRequests) {
    squashOptionText = `The commits from these ${stackedEntriesToMergeCount} branches will be combined into one commit per branch in the base branch.`
  } else if (numberOfCommits === 1) {
    squashOptionText = 'The 1 commit from this branch will be added to the base branch.'
  } else {
    squashOptionText = `The ${numberOfCommits} commits from this branch will be combined into one commit in the base branch.`
  }

  let rebaseOptionText
  if (isMergingMultiplePullRequests) {
    rebaseOptionText = `All commits from these ${stackedEntriesToMergeCount} branches will be rebased and added to the base branch.`
  } else if (numberOfCommits === 1) {
    rebaseOptionText = 'The 1 commit from this branch will be rebased and added to the base branch.'
  } else {
    rebaseOptionText = `The ${numberOfCommits} commits from this branch will be rebased and added to the base branch.`
  }

  return (
    <>
      {isCommandLineInstructionsOpen && returnFocusToCommandLineInstructionsButtonRef && (
        // Wrap this in a Suspense boundary so that we can lazy load the dialog without disrupting the rest of the merge box
        <Suspense>
          <CommandLineInstructionsDialog
            baseRefName={baseRefName}
            conflictsCondition={conflictsCondition}
            headRepository={headRepository}
            isCrossRepo={isCrossRepo}
            returnFocusRef={returnFocusToCommandLineInstructionsButtonRef}
            onClose={() => setIsCommandLineInstructionsOpen(false)}
          />
        </Suspense>
      )}
      <MergeSectionActions>
        <MergeSectionActions.Slot>
          <ButtonWithDropdown
            count={stackedPRsButtonCount}
            hideSecondaryButton={allowedMergeMethods.length === 1}
            inactive={
              mergeCurrentlyBlockedForStacks || (isAutoMergeAllowed ? false : !mergeable && !isBypassRulesActive)
            }
            inactiveTooltipText={inactiveTooltipText}
            inactiveTooltipDirection="se"
            isPrimary={mergeable && mergeBoxRollupStatus === 'ALL_PASSED'}
            secondaryButtonActive
            secondaryButtonAriaLabel="Select merge method"
            shouldFocusPrimaryButton={shouldFocusPrimaryMergeButton}
            actionList={
              <ActionList selectionVariant="single" showDividers>
                {canCreateMergeCommit && (
                  <MergeDropdownOption
                    primaryText="Create a merge commit"
                    secondaryText={mergeOptionText}
                    selected={mergeMethod === MergeMethod.MERGE}
                    onSelect={() =>
                      updateMergeMethod(MergeMethod.MERGE, 'direct_merge_section.select_create_a_merge_commit')
                    }
                  />
                )}
                {canSquashMerge && (
                  <MergeDropdownOption
                    primaryText="Squash and merge"
                    secondaryText={squashOptionText}
                    selected={mergeMethod === MergeMethod.SQUASH}
                    onSelect={() =>
                      updateMergeMethod(MergeMethod.SQUASH, 'direct_merge_section.select_squash_and_merge')
                    }
                  />
                )}
                {canRebaseMerge && (
                  <MergeDropdownOption
                    primaryText="Rebase and merge"
                    secondaryText={rebaseOptionText}
                    selected={mergeMethod === MergeMethod.REBASE}
                    onSelect={() =>
                      updateMergeMethod(MergeMethod.REBASE, 'direct_merge_section.select_rebase_and_merge')
                    }
                  />
                )}
              </ActionList>
            }
            onFocusPrimaryButton={() => setShouldFocusPrimaryMergeButton(false)}
            onPrimaryButtonClick={confirmMerge}
          >
            {mergeButtonText({
              mergeMethod,
              confirming: false,
              isBypassMerge: isBypassRulesActive,
              isAutoMergeAllowed,
              isMergingMultiple: isMergingMultiplePullRequests,
            })}
          </ButtonWithDropdown>
        </MergeSectionActions.Slot>
        <MergeSectionActions.Slot>
          <span className="f6 fgColor-muted">
            {canUserPushToBase ? (
              !isStacked && (
                <>
                  You can also merge this with the command line.{' '}
                  <Link
                    as="button"
                    ref={returnFocusToCommandLineInstructionsButtonRef}
                    inline
                    onClick={handleOpenCommandLineInstructions}
                  >
                    View command line instructions.
                  </Link>
                </>
              )
            ) : (
              <>
                Only those with{' '}
                <Link href={`${helpUrl}/en/get-started/learning-about-github/access-permissions-on-github`} inline>
                  write access
                </Link>{' '}
                to this repository can merge pull requests.
              </>
            )}
          </span>
        </MergeSectionActions.Slot>
        {isStacked && (
          <MergeSectionActions.Slot>
            <PreviewFeedbackMenu />
          </MergeSectionActions.Slot>
        )}
      </MergeSectionActions>
    </>
  )
}
